import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import apiClient from 'src/helpers/apiClient';
import { getFormattedErr } from 'src/helpers';
import type {
  EnrichHiringBody,
  EstimateRevealBody,
  RevealProspectsBody,
  SearchProspectsBody,
  SendOutreachEmailsBody,
} from 'src/openapi';

// Coarse role buckets for filtering prospects by their (freeform) title.
// Values must match the backend RoleCategory enum.
export const ROLE_CATEGORY_OPTIONS: { label: string; value: string }[] = [
  { label: 'Engineering', value: 'Engineering' },
  { label: 'HR / Recruiting', value: 'HR' },
  { label: 'Founder / CXO', value: 'Founder' },
  { label: 'Product', value: 'Product' },
  { label: 'Data', value: 'Data' },
  { label: 'Sales / Marketing', value: 'Sales' },
  { label: 'Other', value: 'Other' },
];

export interface GetProspectsArgs {
  page?: number;
  pageSize?: number;
  hiringStatus?: string;
  hiringType?: string;
  isStaffingFirm?: boolean;
  minValueScore?: number;
  sort?: string;
  sortDir?: string;
  revealStatus?: string;
  searchBatchId?: string;
  hasEmail?: boolean;
  searchQuery?: string;
  roleCategory?: string;
  includeIgnored?: boolean;
  hasAccount?: boolean;
}

export const searchProspects = createAsyncThunk(
  'outreach/searchProspects',
  async (body: SearchProspectsBody, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiSearchProspects({ searchProspectsBody: body });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const getProspects = createAsyncThunk(
  'outreach/getProspects',
  async (args: GetProspectsArgs, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiListProspects({
        hasAccount: args.hasAccount,
        hasEmail: args.hasEmail,
        hiringStatus: args.hiringStatus,
        hiringType: args.hiringType,
        includeIgnored: args.includeIgnored,
        isStaffingFirm: args.isStaffingFirm,
        minValueScore: args.minValueScore,
        page: args.page,
        pageSize: args.pageSize,
        revealStatus: args.revealStatus,
        roleCategory: args.roleCategory,
        searchBatchId: args.searchBatchId,
        searchQuery: args.searchQuery,
        sort: args.sort,
        sortDir: args.sortDir,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const exportForClaude = createAsyncThunk(
  'outreach/exportForClaude',
  async (
    args: { limit?: number; onlyUnknown?: boolean; searchBatchId?: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiExportForClaude({
        limit: args.limit,
        onlyUnknown: args.onlyUnknown,
        searchBatchId: args.searchBatchId,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const importHiring = createAsyncThunk(
  'outreach/importHiring',
  async (body: EnrichHiringBody, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiEnrichHiring({ enrichHiringBody: body });
      enqueueSnackbar(
        `Hiring updated: ${res.data.updated} (not found: ${res.data.not_found})`,
        { variant: 'success' },
      );
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const estimateReveal = createAsyncThunk(
  'outreach/estimateReveal',
  async (body: EstimateRevealBody, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiEstimateReveal({ estimateRevealBody: body });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const revealProspects = createAsyncThunk(
  'outreach/revealProspects',
  async (
    // `silent` suppresses the per-call toast when draining in a loop (the
    // caller shows one summary instead); it must NOT reach the backend body.
    arg: RevealProspectsBody & { silent?: boolean },
    { rejectWithValue },
  ) => {
    const { silent, ...body } = arg;
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiRevealProspects({ revealProspectsBody: body });
      if (!silent) {
        enqueueSnackbar(
          `Revealed ${res.data.revealed}, failed ${res.data.failed}, credits spent ${res.data.credits_spent}`,
          { variant: 'success' },
        );
      }
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const getOutreachCredits = createAsyncThunk(
  'outreach/getCredits',
  async (_: void, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiCreditSummary();
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

export const setApolloCredits = createAsyncThunk(
  'outreach/setCredits',
  async (credits: number, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiSetCredits({ setCreditsBody: { credits } });
      enqueueSnackbar('Apollo credit balance updated', { variant: 'success' });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const sendOutreachEmails = createAsyncThunk(
  'outreach/sendEmails',
  async (body: SendOutreachEmailsBody, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiSendOutreachEmails({ sendOutreachEmailsBody: body });
      enqueueSnackbar(`Queued ${res.data.queued} emails`, {
        variant: 'success',
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const toggleIgnoreProspect = createAsyncThunk(
  'outreach/toggleIgnore',
  async (prospectId: string, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiToggleIgnoreProspect({ prospectId });
      return { prospectId, is_ignored: res.data.is_ignored };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);
